import { AxiosResponse } from 'axios';
import { generatePath } from 'react-router-dom';
import { call, put, takeLatest } from 'redux-saga/effects';
import { showToast } from '../../actions/toast.action';
import {
  getCampaignSummaryAction,
  loadingCampaignDetailsAction,
} from '../../actions/dhwani/campaign.action';
import {
  createCampaign as createCampaignApi,
  getCampaign as getCampaignApi,
  getCampaignSummaryApi,
  updateCampaign as updateCampaignApi,
} from '../../api/Dhwani/campaign.api';
import {
  CREATE_CAMPAIGN,
  GET_CAMPAIGN_DETAILS,
  GET_CAMPAIGN_SUMMARY,
  UPDATE_CAMPAIGN,
} from '../../constants/ActionTypes';
import { HTTP_STATUS_CODES } from '../../constants/HttpConstants';
import { RELATIVE_ROUTES, ROUTES } from '../../constants/RouteConstants';
import { errorHandler } from '../utils.saga';
import { EToastVariant } from 'do-front-end-utils';
import {
  ICreateCampaignAction,
  IUpdateCampaignAction,
  IGetCampaignDetailsAction,
  IGetCampaignSummaryAction,
} from '../../types/dhwani/campaign.types';

/**
 * Helper function to check if FormData needs to be merged with current data
 * @param {FormData} formData - The update FormData
 * @returns {boolean} - Whether full campaign data is needed
 */
function needsFullCampaignData(formData: FormData): boolean {
  const requiredFields = [
    'name',
    'goal',
    'job_role',
    'intro_message',
    'outro_message',
    'start_datetime',
    'end_datetime',
    'channel',
    'calling_hours',
  ];

  // Check if all required fields are present in FormData
  return !requiredFields.every((field) => formData.has(field));
}

/**
 * Helper function to merge current campaign data with FormData updates
 * @param {Object} currentData - Current campaign data
 * @param {FormData} updates - Update FormData
 * @returns {FormData} - Complete FormData with merged data
 */
function mergeWithCurrentData(
  currentData: Record<string, unknown>,
  updates: FormData
): FormData {
  const mergedFormData = new FormData();

  // Add all fields from current data
  mergedFormData.append('name', (currentData.name as string) || '');
  if (currentData.goal) {
    mergedFormData.append('goal', currentData.goal as string);
  }
  mergedFormData.append('job_role', (currentData.job_role as string) || '');
  mergedFormData.append(
    'intro_message',
    (currentData.intro_message as string) || ''
  );
  mergedFormData.append(
    'outro_message',
    (currentData.outro_message as string) || ''
  );
  mergedFormData.append(
    'start_datetime',
    (currentData.start_datetime as string) || ''
  );
  mergedFormData.append(
    'end_datetime',
    (currentData.end_datetime as string) || ''
  );
  mergedFormData.append('channel', (currentData.channel as string) || '');

  // Carry the stored language over as-is. It is nullable and an omitted field
  // keeps its stored value, so only send it if it is set - sending a default
  // would overwrite an unset field.
  if (currentData.language && !updates.has('language')) {
    mergedFormData.append('language', currentData.language as string);
  }
  mergedFormData.append(
    'retries_count',
    String(currentData.retries_count || 1)
  );
  mergedFormData.append(
    'calling_hours',
    (currentData.calling_hours as string) || ''
  );
  if (currentData.time_slot_from) {
    mergedFormData.append(
      'time_slot_from',
      currentData.time_slot_from as string
    );
  }
  if (currentData.time_slot_to) {
    mergedFormData.append('time_slot_to', currentData.time_slot_to as string);
  }

  // Add email data from current data using bracket notation if not in updates
  if (
    !updates.has('email_data[candidate_template][header]') &&
    currentData.email_data
  ) {
    const emailData = currentData.email_data as {
      candidate_template?: {
        header?: string;
        subject?: string;
        body?: string;
      };
    };
    if (emailData.candidate_template) {
      if (emailData.candidate_template.header) {
        mergedFormData.append(
          'email_data[candidate_template][header]',
          emailData.candidate_template.header
        );
      }
      if (emailData.candidate_template.subject) {
        mergedFormData.append(
          'email_data[candidate_template][subject]',
          emailData.candidate_template.subject
        );
      }
      if (emailData.candidate_template.body) {
        mergedFormData.append(
          'email_data[candidate_template][body]',
          emailData.candidate_template.body
        );
      }
    }
  }

  // Override with values from updates FormData
  updates.forEach((value, key) => {
    // Remove old value and add new one
    mergedFormData.delete(key);
    mergedFormData.append(key, value);
  });

  return mergedFormData;
}

/**
 * Create Campaign Saga
 * @param {ICreateCampaignAction} data - The campaign action
 * @returns {void}
 */
function* createCampaign(data: ICreateCampaignAction) {
  try {
    yield put({
      type: 'SHOW_LOADER',
      payload: {
        show: true,
      },
    });

    const { navigate, payload, isNext } = data.payload;

    // payload is FormData
    const createCampaignResp: AxiosResponse = yield call(() =>
      createCampaignApi(payload)
    );

    if (
      createCampaignResp &&
      createCampaignResp.status === HTTP_STATUS_CODES.CREATED &&
      createCampaignResp.data
    ) {
      const createdCampaign = createCampaignResp.data.data;

      yield put({
        type: `${CREATE_CAMPAIGN}_SUCCESS`,
        payload: createdCampaign,
      });

      yield put(
        showToast({
          id: 'drd-dhwani-campaign-created-toast',
          message: 'Campaign created successfully',
          variant: EToastVariant.SUCCESS,
        })
      );

      // Fetch campaign summary after successful creation
      if (createdCampaign && createdCampaign.slug) {
        yield put(getCampaignSummaryAction(createdCampaign.slug));

        // Redirect to campaign details page
        navigate(generatePath(RELATIVE_ROUTES.DHWANI));

        if (isNext) {
          navigate(
            generatePath(ROUTES.DHWANI_CAMPAIGN_QUESTIONS_ROUTE, {
              campaignSlug: createdCampaign.slug,
            })
          );
        }
      }
    } else {
      yield put(
        showToast({
          id: 'drd-dhwani-campaign-error-toast',
          message: createCampaignResp.data.message,
          variant: EToastVariant.ERROR,
        })
      );
    }
  } catch (err) {
    const message: string | null = yield call(errorHandler, err);
    if (message) {
      yield put(
        showToast({
          id: 'drd-dhwani-campaign-error-toast',
          message,
          variant: EToastVariant.ERROR,
        })
      );

      yield put(
        showToast({
          id: 'drd-dhwani-campaign-error-toast',
          message: 'Failed to create campaign',
          variant: EToastVariant.ERROR,
        })
      );
    }
  } finally {
    yield put({
      type: 'HIDE_LOADER',
    });
  }
}

/**
 * Update Campaign Saga
 * @param {IUpdateCampaignAction} data - Contains campaign slug and update data
 * @returns {void}
 */
function* updateCampaign(data: IUpdateCampaignAction) {
  try {
    yield put({
      type: 'SHOW_LOADER',
      payload: {
        show: true,
      },
    });

    const {
      navigate,
      campaignSlug,
      payload,
      isNext,
      redirectToCampaignDashboard,
    } = data.payload;

    // payload is FormData
    let completePayload = payload;

    // For partial updates, first get current campaign data
    if (needsFullCampaignData(completePayload)) {
      const currentCampaign: AxiosResponse = yield call(() =>
        getCampaignApi(campaignSlug)
      );

      if (currentCampaign.status === HTTP_STATUS_CODES.OK) {
        // Extract necessary fields from current campaign
        const campaignData = currentCampaign.data.data;
        completePayload = mergeWithCurrentData(campaignData, completePayload);
      }
    }

    const updateCampaignResp: AxiosResponse = yield call(() =>
      updateCampaignApi(campaignSlug, completePayload)
    );

    if (
      updateCampaignResp &&
      updateCampaignResp.status === HTTP_STATUS_CODES.OK &&
      updateCampaignResp.data
    ) {
      yield put({
        type: `${UPDATE_CAMPAIGN}_SUCCESS`,
        payload: updateCampaignResp.data.data,
      });

      yield put(
        showToast({
          id: 'drd-dhwani-campaign-updated-toast',
          message: 'Campaign updated successfully',
          variant: EToastVariant.SUCCESS,
        })
      );

      // Fetch campaign summary after successful update
      yield put(getCampaignSummaryAction(campaignSlug));

      if (isNext) {
        navigate(
          generatePath(ROUTES.DHWANI_CAMPAIGN_QUESTIONS_ROUTE, {
            campaignSlug: campaignSlug,
          })
        );
      }

      if (redirectToCampaignDashboard) {
        navigate(RELATIVE_ROUTES.DHWANI);
      }
    } else {
      yield put(
        showToast({
          id: 'drd-dhwani-campaign-error-toast',
          message: updateCampaignResp.data.message,
          variant: EToastVariant.ERROR,
        })
      );
    }
  } catch (err) {
    const message: string | null = yield call(errorHandler, err);
    if (message) {
      yield put(
        showToast({
          id: 'drd-dhwani-campaign-error-toast',
          message,
          variant: EToastVariant.ERROR,
        })
      );

      yield put(
        showToast({
          id: 'drd-dhwani-campaign-error-toast',
          message: 'Failed to update campaign',
          variant: EToastVariant.ERROR,
        })
      );
    }
  } finally {
    yield put({
      type: 'HIDE_LOADER',
    });
  }
}

/**
 * Get Campaign Details Saga
 * @param {IGetCampaignDetailsAction} data - Contains campaign slug
 * @returns {void}
 */
function* getCampaignDetails(data: IGetCampaignDetailsAction) {
  try {
    yield put(loadingCampaignDetailsAction());

    yield put({
      type: 'SHOW_LOADER',
      payload: {
        show: true,
      },
    });

    const { campaignSlug } = data.payload;
    const getCampaignResp: AxiosResponse = yield call(() =>
      getCampaignApi(campaignSlug)
    );

    if (
      getCampaignResp &&
      getCampaignResp.status === HTTP_STATUS_CODES.OK &&
      getCampaignResp.data
    ) {
      yield put({
        type: `${GET_CAMPAIGN_DETAILS}_SUCCESS`,
        payload: getCampaignResp.data.data,
      });
    } else {
      yield put(
        showToast({
          id: 'drd-dhwani-campaign-error-toast',
          message: getCampaignResp.data.message,
          variant: EToastVariant.ERROR,
        })
      );
    }
  } catch (err) {
    const message: string | null = yield call(errorHandler, err);
    if (message) {
      yield put(
        showToast({
          id: 'drd-dhwani-campaign-error-toast',
          message,
          variant: EToastVariant.ERROR,
        })
      );
    }
  } finally {
    yield put({
      type: 'HIDE_LOADER',
    });
  }
}

/**
 * Get Campaign Summary Saga
 * @param {IGetCampaignSummaryAction} data - Contains campaign slug
 * @returns {void}
 */
function* getCampaignSummary(data: IGetCampaignSummaryAction) {
  try {
    const { campaignSlug } = data.payload;
    const response: AxiosResponse = yield call(() =>
      getCampaignSummaryApi(campaignSlug)
    );

    if (
      response &&
      response.status === HTTP_STATUS_CODES.OK &&
      response.data?.data
    ) {
      yield put({
        type: `${GET_CAMPAIGN_SUMMARY}_SUCCESS`,
        payload: response.data.data,
      });
    } else {
      yield put({
        type: `${GET_CAMPAIGN_SUMMARY}_FAILURE`,
        payload: response.data?.message || 'Failed to get campaign summary',
      });
    }
  } catch (err) {
    const message: string | null = yield call(errorHandler, err);
    if (message) {
      yield put({
        type: `${GET_CAMPAIGN_SUMMARY}_FAILURE`,
        payload: message,
      });
    }
  }
}

/**
 * Campaign Saga
 * @returns {void}
 */
export default function* campaignSaga() {
  yield takeLatest(CREATE_CAMPAIGN, createCampaign);
  yield takeLatest(UPDATE_CAMPAIGN, updateCampaign);
  yield takeLatest(GET_CAMPAIGN_DETAILS, getCampaignDetails);
  yield takeLatest(GET_CAMPAIGN_SUMMARY, getCampaignSummary);
}
